package com.siriusxm.unifiedcontent.console.aggregator

import com.siriusxm.pia.CapabilityAction
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.pia.views.unifiedaggregator.toEntityType
import contentingestion.unifiedmodel.EntityType
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import smithy4kt.SmithyError
import unifiedcontent.aggregator.PartialSchema
import unifiedcontent.aggregator.ProducerDetails

/*
 * Client-side counterparts of the aggregator's UCP intake checks (catalog PR #710, specs/producers.md).
 *
 * The console no longer names a producer when it publishes a partial or edits a relationship. The
 * aggregator resolves the producer from the access token's Cognito client id, which is the console's
 * own `clientId` from config.json, and for a producer whose `permissionModel` is UCP it authorizes the
 * *caller* through AVP. Everything here only decides what the UI offers; the aggregator is the gate.
 */

/** AVP action the aggregator checks on `POST /incoming/partial` for a UCP producer (UCA-1107). */
internal val PartialUpdateCapability = CapabilityAction("UnifiedContentAggregator_PartialUpdate")

/**
 * AVP action the aggregator checks on relationship create and remove for a UCP producer (UCA-1151).
 * The `operation` context key tells the two apart, so they can be granted independently.
 */
internal val RelationshipUpdateCapability = CapabilityAction("UnifiedContentAggregator_RelationshipUpdate")

/** The `operation` context value for [RelationshipUpdateCapability]: POST is CREATE, DELETE is REMOVE. */
internal enum class RelationshipOperation { CREATE, REMOVE }

/**
 * Build the partial-update question with exactly the server's Cedar context. `partialId` and
 * `entityId` are always present. `entityType`, `showId`, and `channelId` are present only when known,
 * and are omitted rather than set to null, so a policy that needs one must guard it with `context has`.
 */
internal fun partialUpdateCapability(
    partialId: String,
    entityId: String,
    entityType: String? = null,
    showId: String? = null,
    channelId: String? = null
): CapabilityQuery = CapabilityQuery(
    action = PartialUpdateCapability,
    context = buildJsonObject {
        put("partialId", partialId)
        put("entityId", entityId)
        entityType?.takeIf { it.isNotBlank() }?.let { put("entityType", it) }
        showId?.takeIf { it.isNotBlank() }?.let { put("showId", it) }
        channelId?.takeIf { it.isNotBlank() }?.let { put("channelId", it) }
    }
)

/**
 * Build the partial-update question the server will ask for a partial whose `modification` is
 * [modification], derived the same way the server derives it:
 * - `entityId` and `entityType` come from the modification's `id` and `type`;
 * - `showId` / `channelId` come from the *stored* entity's top-level `show` / `channel`, and the server
 *   only looks them up when the modification carries `id`, `type`, and `version`. Pass the entity the
 *   editor loaded as [storedEntity]; null leaves them out, as a failed lookup does on the server.
 *
 * Returns null when the modification has no `id`, because the server rejects that partial with a 400
 * before it authorizes anything.
 */
internal fun partialUpdateCapability(
    partialId: String,
    modification: JsonObject,
    storedEntity: JsonObject?
): CapabilityQuery? {
    val entityId = modification.stringField("id") ?: return null
    val entityType = modification.stringField("type")
    val ancestors = storedEntity?.takeIf { entityType != null && modification.stringField("version") != null }
    return partialUpdateCapability(
        partialId = partialId,
        entityId = entityId,
        entityType = entityType,
        showId = ancestors?.stringField("show"),
        channelId = ancestors?.stringField("channel")
    )
}

/** Build the relationship-edit question with the server's Cedar context. Every key is always present. */
internal fun relationshipUpdateCapability(
    relationshipId: String,
    operation: RelationshipOperation,
    subjectId: String,
    objectId: String
): CapabilityQuery = CapabilityQuery(
    action = RelationshipUpdateCapability,
    context = buildJsonObject {
        put("relationshipId", relationshipId)
        put("operation", operation.name)
        put("subjectId", subjectId)
        put("objectId", objectId)
    }
)

/**
 * The id sent for a side of a relationship that is not chosen yet, when asking whether the user may
 * create relationships at all (see [relationshipCreateAffordance]). Not a real entity id.
 */
internal const val UNCHOSEN_RELATIONSHIP_SIDE = "*"

/**
 * Build the CREATE question used to decide whether to *offer* creating a [relationshipId]
 * relationship, before the user has picked one or both sides. Unknown sides are sent as
 * [UNCHOSEN_RELATIONSHIP_SIDE], so the answer is right for grants that are unconditional or that
 * depend on `relationshipId`, `operation`, or a side that is known. A grant that requires a specific
 * id on an unchosen side reads as denied here and hides the button, even though the user could create
 * that one link. That is an accepted gap: such grants are expected to be rare. The real per-pair
 * check runs before submitting, and the aggregator checks the request itself.
 *
 * @param subjectId the subject when it is fixed by the page (for example the entity being viewed)
 * @param objectId the object when it is fixed by the page
 */
internal fun relationshipCreateAffordance(
    relationshipId: String,
    subjectId: String? = null,
    objectId: String? = null
): CapabilityQuery = relationshipUpdateCapability(
    relationshipId,
    RelationshipOperation.CREATE,
    subjectId ?: UNCHOSEN_RELATIONSHIP_SIDE,
    objectId ?: UNCHOSEN_RELATIONSHIP_SIDE
)

/**
 * The producer the aggregator will resolve for the console's own calls: the one whose Cognito client
 * id is the console's `clientId`. Null when this environment has no such producer.
 */
internal fun consoleProducer(producers: List<ProducerDetails>, consoleClientId: String): ProducerDetails? =
    producers.firstOrNull { it.clientId == consoleClientId }

/**
 * Whether this producer may publish [partialId] onto an entity of type [entityTypeName]. This is the
 * ceiling the aggregator's downstream processor enforces. A partial outside it is accepted by the API
 * with a 200 and then dropped, so the console must not offer it. A partial in `unrestrictedPartials`
 * is allowed on every type (UCA-1162), including types its schema does not list under
 * `typePermissions`. Otherwise this uses the per-type `typePermissions` when the server sent them,
 * and the flat `allowedPartials` otherwise.
 */
internal fun ProducerDetails.allowsPartial(partialId: String, entityTypeName: String): Boolean {
    if (unrestrictedPartials.orEmpty().contains(partialId)) return true
    val perTypePermissions = typePermissions
    return if (!perTypePermissions.isNullOrEmpty()) {
        perTypePermissions.any { it.type == entityTypeName && it.partials.orEmpty().contains(partialId) }
    } else {
        allowedPartials.orEmpty().contains(partialId)
    }
}

/** Whether this producer may create or remove relationships of [relationshipId]. The API enforces this too. */
internal fun ProducerDetails.allowsRelationship(relationshipId: String): Boolean =
    allowedRelationships.orEmpty().contains(relationshipId)

/**
 * The partial schemas the console can publish onto an entity of [entityType]: the schema must support
 * the type, and [producer] (the console producer) must allow the partial for that type. This is only
 * the ceiling; whether a particular user may publish one is a separate AVP question.
 */
internal fun consolePartialsFor(
    producer: ProducerDetails,
    schemas: List<PartialSchema>,
    entityType: EntityType
): List<PartialSchema> = schemas.filter { schema ->
    schema.types.any { it.toEntityType() == entityType } && producer.allowsPartial(schema.id, entityType.name)
}

/** How an intake call failed, as far as the user needs to know. */
internal enum class IntakeFailure {
    /** 403: the caller is not allowed to do this. */
    DENIED,

    /** 503: the aggregator could not get an authorization decision, so it refused (fails closed). */
    UNAVAILABLE,

    /** Anything else. */
    OTHER
}

/** Classify an exception thrown by the aggregator SDK for a partial or relationship call. */
internal fun classifyIntakeFailure(error: Throwable): IntakeFailure =
    when ((error as? SmithyError)?.httpStatusCode) {
        403 -> IntakeFailure.DENIED
        503 -> IntakeFailure.UNAVAILABLE
        else -> IntakeFailure.OTHER
    }

/** A notification for a failed intake call: a headline and the server's detail. */
internal data class IntakeErrorMessage(val message: String, val description: String?)

/**
 * The notification to show when [operation] (phrased to follow "to", e.g. "publish this partial")
 * fails. A 403 and a 503 read differently on purpose: a 403 means "you can't", and a 503 means "we
 * couldn't check, try again", which is not something the user's permissions can fix.
 */
internal fun intakeErrorMessage(operation: String, error: Throwable): IntakeErrorMessage =
    when (classifyIntakeFailure(error)) {
        IntakeFailure.DENIED -> IntakeErrorMessage(
            "You do not have permission to $operation.",
            error.message
        )

        IntakeFailure.UNAVAILABLE -> IntakeErrorMessage(
            "Permissions could not be checked right now, so nothing was changed. Try again in a few minutes.",
            error.message
        )

        IntakeFailure.OTHER -> IntakeErrorMessage("Unable to $operation.", error.message)
    }

/** A string-valued top-level field, or null when it is absent, blank, or not a string. */
private fun JsonObject.stringField(key: String): String? =
    (this[key] as? JsonPrimitive)?.takeIf { it.isString }?.content?.takeIf { it.isNotBlank() }
